import { CSSProperties, ReactNode } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import SettingsIcon from '@mui/icons-material/Settings';
import Typography from '@mui/material/Typography';
import { JobStatusType } from 'src/pages/dashboard/util';

const commonStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: '0.1875rem',
  alignItems: 'center',
  padding: '0.1875rem',
  justifyContent: 'center',
};

interface JobStatusProps {
  status: JobStatusType;
  width?: string;
  endAdornment?: ReactNode;
  disabled?: boolean;
  sx?: CSSProperties;
  onClick?: () => void | null;
}

const JobStatus = ({ status, width, sx, disabled, endAdornment, onClick }: JobStatusProps) => {
  const statusConfig = {
    completed: {
      background: '#e8fcf1',
      IconComponent: CheckCircleIcon,
      text: 'Complete',
      color: '#0F3010',
    },
    in_queue: {
      background: '#ffeedf',
      IconComponent: PendingIcon,
      text: 'In queue',
      color: '#7B3F07',
    },
    cancelled: {
      background: '#e5e4e5',
      IconComponent: CancelIcon,
      text: 'Cancelled',
      color: '#49454F',
    },
    on_hold: {
      background: '#ffe8e7',
      IconComponent: ReportProblemIcon,
      text: 'On-Hold',
      color: '#5E0A05',
    },
    on_hold_manual: {
      background: '#ffe8e7',
      IconComponent: ReportProblemIcon,
      text: 'On-Hold',
      color: '#5E0A05',
    },
    processing: {
      background: '#eaeeff',
      IconComponent: SettingsIcon,
      text: 'Processing',
      color: '#140838',
    },
    printing: {
      background: '#eaeeff',
      IconComponent: SettingsIcon,
      text: 'Printing',
      color: '#140838',
    },
    completed_payment_declined: {
      background: '#eaeeff',
      IconComponent: CheckCircleIcon,
      text: 'Complete - payment declined',
      color: '#0F3010',
    },
    manual_review: {
      background: '#ffe8e7',
      IconComponent: ReportProblemIcon,
      text: 'Manual Review',
      color: '#5E0A05',
    },
    draft: {
      background: '#e5e4e5',
      IconComponent: PendingIcon,
      text: 'Draft',
      color: '#49454F',
    },
    submitted: {
      background: '#ffeedf',
      IconComponent: PendingIcon,
      text: 'Submitted',
      color: '#7B3F07',
    },
  };

  const renderStatus = (config: { background: string; IconComponent: any; text: string; color: string }) => {
    const { background, IconComponent, text, color } = config;
    return (
      <div
        style={{
          ...commonStyle,
          ...sx,
          background: disabled ? 'grey' : background,
          border: 'none',
          borderRadius: '1.25rem',
          pointerEvents: disabled ? 'none' : 'auto',
          width,
        }}
        onClick={onClick}
      >
        <IconComponent sx={{ fontSize: '0.875rem', color: disabled ? 'black' : color }} />
        <Typography sx={{ fontSize: '0.875rem', color: disabled ? 'black' : color }}>{text}</Typography>
        {endAdornment}
      </div>
    );
  };

  // Fallback for any status not explicitly mapped (e.g. draft/submitted or a
  // future status). Without this, statusConfig[status] is undefined and
  // renderStatus crashes on destructure — white-screening the whole page.
  const config = statusConfig[status] ?? {
    background: '#e5e4e5',
    IconComponent: PendingIcon,
    text: status ?? 'Unknown',
    color: '#49454F',
  };
  return renderStatus(config);
};

export default JobStatus;
